import {defineStore} from "pinia";
import environment from "@/environment";
import httpClient from "@/tools/http-client";
import {ref} from "vue";

const adminEndpoints = {
    users: environment.appConfig.baseApiUrl + "admin/users",
    news: environment.appConfig.baseApiUrl + "admin/news",
    products: environment.appConfig.baseApiUrl + "admin/products",
    categories: environment.appConfig.baseApiUrl + "admin/categories",
    'custom-pages': environment.appConfig.baseApiUrl + "admin/custom-pages",
    orders: environment.appConfig.baseApiUrl + "admin/orders",
    'price-list-items': environment.appConfig.baseApiUrl + "admin/price-list-items",
    coins: environment.appConfig.baseApiUrl + "admin/coins",
    mailings: environment.appConfig.baseApiUrl + "admin/mailings",
    roles: environment.appConfig.baseApiUrl + "admin/roles",
    system: environment.appConfig.baseApiUrl + "admin/system/status",
    systemActiveSessions: environment.appConfig.baseApiUrl + "admin/system/active-sessions",
    systemSessions: environment.appConfig.baseApiUrl + "admin/system/sessions",
};

export const useAdminStore = defineStore('adminStore', () => {
    const users = ref([]);
    const items = ref([]);
    const news = ref([]);
    const products = ref([]);
    const customPages = ref([]);
    const orders = ref([]);
    const priceListItems = ref([]);
    const coins = ref([]);
    const mailings = ref([]);
    const loading = ref(false);
    const error = ref("");
    const total = ref(null);
    const totalNews = ref(null);
    const totalCoins = ref(null);
    const totalProducts = ref(null);
    const categories = ref([]);
    const roles = ref([]);
    const systemStatus = ref(null);

    function startLoading() {
        loading.value = true;
        error.value = "";
    }

    function clearLoading() {
        loading.value = false;
        error.value = "";
    }

    function setError(e) {
        if (e?.response?.data?.message) {
            error.value = e.response.data.message;
        } else if (e?.message) {
            error.value = e.message;
        } else {
            error.value = JSON.stringify(e);
        }
        loading.value = false;
    }

    function endpoint(kind) {
        return adminEndpoints[kind] || adminEndpoints.users;
    }

    async function loadData(params = {}) {
        startLoading();
        try {
            const resp = await httpClient.get(adminEndpoints.users, {params: {per_page: 50, ...params}});
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return;
            }
            if (resp.data?.total !== undefined) {
                total.value = resp.data.total;
            }
            items.value = resp.data?.data || [];
            users.value = items.value;
            clearLoading();
        } catch (e) {
            setError(e);
        }
    }

    async function loadNewsData(params = {}) {
        startLoading();
        try {
            const resp = await httpClient.get(adminEndpoints.news, {params: {per_page: 50, ...params}});
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return;
            }
            if (resp.data?.total !== undefined) {
                totalNews.value = resp.data.total;
            }
            news.value = resp.data?.data || [];
            clearLoading();
        } catch (e) {
            setError(e);
        }
    }

    async function loadProductsData(params = {}) {
        startLoading();
        try {
            const resp = await httpClient.get(adminEndpoints.products, {params: {per_page: 50, ...params}});
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return;
            }
            if (resp.data?.total !== undefined) {
                totalProducts.value = resp.data.total;
            }
            products.value = resp.data?.data || [];
            clearLoading();
        } catch (e) {
            setError(e);
        }
    }

    async function loadMailings(params = {}) {
        startLoading();
        try { const resp = await httpClient.get(adminEndpoints.mailings, {params: {per_page: 50, ...params}}); mailings.value = resp.data?.data || []; total.value = resp.data?.total ?? 0; clearLoading(); return mailings.value; }
        catch (e) { setError(e); return []; }
    }
    // Подсчёт получателей — лёгкий запрос: не переключаем глобальный preloader
    async function loadRecipientsCount(params = {}) {
        try { const resp = await httpClient.get(adminEndpoints.mailings + "/recipients/count", {params}); error.value = ""; return resp.data?.count ?? 0; }
        catch (e) { setError(e); return null; }
    }
    async function createMailing(payload) { return requestMailing('post', adminEndpoints.mailings, payload); }
    async function updateMailing(id, payload) { return requestMailing('put', adminEndpoints.mailings + '/' + id, payload); }
    // Подпись по умолчанию для новых рассылок (страница custom_pages со slug = default-sign)
    async function loadDefaultSignature() {
        try {
            const resp = await httpClient.get(environment.appConfig.baseApiUrl + "custom-page", {params: {page_slug: "default-sign"}});
            return resp.data?.custom_page?.content || "";
        } catch (e) { return ""; }
    }
    async function requestMailing(method, url, payload) {
        startLoading();
        try { const resp = await httpClient[method](url, payload); clearLoading(); return resp.data?.data || true; }
        catch (e) { setError(e); return null; }
    }
    async function testMailing(id, email) { return !!(await requestMailing('post', adminEndpoints.mailings + '/' + id + '/test', {email})); }
    async function sendMailing(id, mode = 'all') { return requestMailing('post', adminEndpoints.mailings + '/' + id + '/send', {mode}); }
    async function loadMailingRecipientsCount(id, mode = 'all') {
        try { const resp = await httpClient.get(adminEndpoints.mailings + '/' + id + '/recipients/count', {params: {mode}}); error.value = ""; return resp.data?.count ?? 0; }
        catch (e) { setError(e); return null; }
    }
    async function loadMailingRuns(id) {
        startLoading();
        try { const resp = await httpClient.get(adminEndpoints.mailings + '/' + id + '/runs'); clearLoading(); return resp.data?.data || []; }
        catch (e) { setError(e); return []; }
    }
    async function loadMailingDeliveries(id, params = {}) {
        startLoading();
        try {
            const resp = await httpClient.get(adminEndpoints.mailings + '/' + id + '/deliveries', {params: {per_page: 50, ...params}});
            clearLoading();
            return {data: resp.data?.data || [], total: resp.data?.total ?? 0};
        } catch (e) { setError(e); return {data: [], total: 0}; }
    }

    async function loadCoinsData(params = {}) {
        startLoading();
        try {
            const resp = await httpClient.get(adminEndpoints.coins, {params: {per_page: 50, ...params}});
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return;
            }
            if (resp.data?.total !== undefined) {
                totalCoins.value = resp.data.total;
            }
            coins.value = resp.data?.data || [];
            clearLoading();
        } catch (e) {
            setError(e);
        }
    }

    function clearData() {
        users.value = [];
        items.value = [];
        total.value = null;
        totalNews.value = null;
    }

    function resetData() {
        users.value = [];
        items.value = [];
        total.value = null;
        totalNews.value = null;
    }

    async function loadList(kind, params = {}) {
        startLoading();
        try {
            let config = {};
            if (kind === 'users') {
                config = {params: {per_page: 50, ...params}};
            } else if (kind === 'orders') {
                config = {params: {per_page: 50, ...params}};
            } else {
                config = {params: {per_page: 1000}};
            }
            const resp = await httpClient.get(endpoint(kind), config);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return;
            }
            const items = resp.data?.data || [];
            if (resp.data?.total !== undefined) {
                total.value = resp.data.total;
            }
            if (kind === 'users') {
                users.value = items;
                items.value = items;
            }
            else if (kind === 'news') news.value = items;
            else if (kind === 'products') products.value = items;
            else if (kind === 'custom-pages') customPages.value = items;
            else if (kind === 'orders') orders.value = items;
            else if (kind === 'price-list-items') priceListItems.value = items;
            else if (kind === 'coins') coins.value = items;
            clearLoading();
        } catch (e) {
            setError(e);
        }
    }

    async function loadCategories() {
        startLoading();
        try {
            const resp = await httpClient.get(adminEndpoints.categories);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return;
            }
            categories.value = resp.data?.data || [];
            clearLoading();
        } catch (e) {
            setError(e);
        }
    }

    async function createItem(kind, payload) {
        startLoading();
        try {
            const resp = await httpClient.post(endpoint(kind), payload);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return null;
            }
            clearLoading();
            return resp.data?.data || null;
        } catch (e) {
            setError(e);
            return null;
        }
    }

    async function updateItem(kind, id, payload) {
        startLoading();
        try {
            const resp = await httpClient.put(endpoint(kind) + '/' + id, payload);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return null;
            }
            clearLoading();
            return resp.data?.data || null;
        } catch (e) {
            setError(e);
            return null;
        }
    }

    async function uploadNewsImage(id, file) {
        startLoading();
        try {
            const body = new FormData();
            body.append('image', file);
            await httpClient.post(`${adminEndpoints.news}/${id}/image`, body);
            clearLoading();
            return true;
        } catch (e) {
            setError(e);
            return false;
        }
    }

    async function uploadProductImage(id, slot, file) {
        startLoading();
        try {
            const body = new FormData();
            body.append('image', file);
            await httpClient.post(`${adminEndpoints.products}/${id}/images/${slot}`, body);
            clearLoading();
            return true;
        } catch (e) {
            setError(e);
            return false;
        }
    }

    async function uploadUserImage(id, file) {
        startLoading();
        try {
            const body = new FormData();
            body.append('image', file);
            await httpClient.post(`${adminEndpoints.users}/${id}/image`, body);
            clearLoading();
            return true;
        } catch (e) {
            setError(e);
            return false;
        }
    }

    async function deleteItem(kind, id) {
        startLoading();
        try {
            const resp = await httpClient.delete(endpoint(kind) + '/' + id);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return false;
            }
            clearLoading();
            return true;
        } catch (e) {
            setError(e);
            return false;
        }
    }

    async function resetPassword(id, password) {
        startLoading();
        try {
            const resp = await httpClient.patch(endpoint('users') + '/' + id + '/password', {password});
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return false;
            }
            clearLoading();
            return true;
        } catch (e) {
            setError(e);
            return false;
        }
    }

    async function loadRoles() {
        startLoading();
        try {
            const resp = await httpClient.get(adminEndpoints.roles);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return [];
            }
            roles.value = resp.data?.data || [];
            clearLoading();
            return roles.value;
        } catch (e) {
            setError(e);
            return [];
        }
    }

    async function updateUserRoles(id, rolesList) {
        startLoading();
        try {
            const resp = await httpClient.patch(endpoint('users') + '/' + id + '/roles', {roles: rolesList});
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return null;
            }
            clearLoading();
            return resp.data?.data || null;
        } catch (e) {
            setError(e);
            return null;
        }
    }

    async function createRole(payload) {
        startLoading();
        try {
            const resp = await httpClient.post(adminEndpoints.roles, payload);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return null;
            }
            clearLoading();
            return resp.data?.data || null;
        } catch (e) {
            setError(e);
            return null;
        }
    }

    async function updateRole(id, payload) {
        startLoading();
        try {
            const resp = await httpClient.put(adminEndpoints.roles + '/' + id, payload);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return null;
            }
            clearLoading();
            return resp.data?.data || null;
        } catch (e) {
            setError(e);
            return null;
        }
    }

    async function deleteRole(id) {
        startLoading();
        try {
            const resp = await httpClient.delete(adminEndpoints.roles + '/' + id);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return false;
            }
            clearLoading();
            return true;
        } catch (e) {
            setError(e);
            return false;
        }
    }

    async function loadSystemStatus() {
        startLoading();
        try {
            const resp = await httpClient.get(adminEndpoints.system);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return null;
            }
            systemStatus.value = resp.data?.data || null;
            clearLoading();
            return systemStatus.value;
        } catch (e) {
            setError(e);
            return null;
        }
    }

    async function loadActiveSessions() {
        try {
            const resp = await httpClient.get(adminEndpoints.systemActiveSessions);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return [];
            }
            // Успех сбрасывает глобальную ошибку: иначе компонент показал бы
            // устаревший toast от предышедшей неудачной операции стора.
            error.value = "";
            return resp.data?.data || [];
        } catch (e) {
            setError(e);
            return [];
        }
    }

    async function loadSessionDetails() {
        try {
            const resp = await httpClient.get(adminEndpoints.systemSessions);
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return [];
            }
            error.value = "";
            return resp.data?.data || [];
        } catch (e) {
            setError(e);
            return [];
        }
    }

    async function updateOrderStatus(id, status) {
        startLoading();
        try {
            const resp = await httpClient.patch(endpoint('orders') + '/' + id + '/status', {status});
            if (resp.data?.status === 'error') {
                setError(resp.data.message);
                return null;
            }
            clearLoading();
            return resp.data?.data || null;
        } catch (e) {
            setError(e);
            return null;
        }
    }

    return {
        users,
        items,
        news,
        products,
        customPages,
        orders,
        priceListItems,
        coins,
        mailings,
        categories,
        loading,
        error,
        total,
        totalNews,
        totalCoins,
        totalProducts,
        loadData,
        loadNewsData,
        loadProductsData,
        loadMailings,
        loadRecipientsCount,
        createMailing,
        updateMailing,
        loadDefaultSignature,
        testMailing,
        sendMailing,
        loadMailingRecipientsCount,
        loadMailingRuns,
        loadMailingDeliveries,
        loadCoinsData,
        loadCategories,
        clearData,
        resetData,
        loadList,
        createItem,
        updateItem,
        uploadNewsImage,
        uploadProductImage,
        uploadUserImage,
        deleteItem,
        resetPassword,
        loadRoles,
        updateUserRoles,
        createRole,
        updateRole,
        deleteRole,
        roles,
        systemStatus,
        loadSystemStatus,
        loadActiveSessions,
        loadSessionDetails,
        updateOrderStatus,
    };
});
